import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import {
  CreditCard, Users, TrendingUp, DollarSign, Clock,
  ChevronDown, ChevronRight, ExternalLink, AlertCircle
} from "lucide-react";
import { Avatar, AvatarFallback } from "../components/ui/avatar";

// ─── helpers ────────────────────────────────────────────────────────────────

const getInitials = (name) =>
  name ? name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2) : "?";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");

const STATUS_META = {
  trialing:   { label: "Trialing",   cls: "bg-blue-500/15 text-blue-400 border-blue-500/30" },
  trial:      { label: "Trial",      cls: "bg-blue-500/15 text-blue-400 border-blue-500/30" },
  active:     { label: "Active",     cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
  cancelling: { label: "Cancelling", cls: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  paused:     { label: "Paused",     cls: "bg-orange-500/15 text-orange-400 border-orange-500/30" },
  cancelled:  { label: "Cancelled",  cls: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30" },
  expired:    { label: "Expired",    cls: "bg-red-500/15 text-red-400 border-red-500/30" },
};

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || { label: status, cls: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30" };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${m.cls}`}>
      {m.label}
    </span>
  );
};

const CardDisplay = ({ brand, last4, hasPm }) => {
  if (!hasPm) return <span className="text-zinc-500 text-xs">No card</span>;
  if (brand && last4) return (
    <span className="text-xs text-zinc-300 capitalize">{brand} ···· {last4}</span>
  );
  return <span className="text-xs text-emerald-400">On file</span>;
};

const FILTERS = [
  { key: null,           label: "All" },
  { key: "trial",        label: "Trial" },
  { key: "active",       label: "Active paid" },
  { key: "no_payment_method", label: "No card" },
  { key: "cancelling",   label: "Cancelling" },
  { key: "paused_expired", label: "Paused / Expired" },
];

// ─── expandable row ──────────────────────────────────────────────────────────

const SubRow = ({ row }) => {
  const [open, setOpen] = useState(false);
  const stripeBase = `https://dashboard.stripe.com/${row.stripe_env}`;

  return (
    <>
      <tr
        className="border-t border-zinc-800 hover:bg-zinc-800/40 cursor-pointer transition-colors"
        onClick={() => setOpen((o) => !o)}
        data-testid={`sub-row-${row.user_id}`}
      >
        {/* User */}
        <td className="px-4 py-3">
          <div className="flex items-center gap-3">
            <Avatar className="w-7 h-7 border border-zinc-700 flex-shrink-0">
              <AvatarFallback className="bg-zinc-800 text-zinc-400 text-xs">
                {getInitials(row.name)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="text-sm text-white truncate">{row.name}</p>
              <p className="text-xs text-zinc-500 truncate">{row.email}</p>
            </div>
          </div>
        </td>
        {/* Status */}
        <td className="px-4 py-3">
          <StatusBadge status={row.display_status} />
        </td>
        {/* Payment method */}
        <td className="px-4 py-3">
          <CardDisplay brand={row.card_brand} last4={row.card_last4} hasPm={row.has_payment_method} />
        </td>
        {/* Trial end / next billing */}
        <td className="px-4 py-3 text-xs text-zinc-400 whitespace-nowrap">
          {row.display_status === "trialing" || row.display_status === "trial" ? (
            <span>
              {fmt(row.trial_end)}
              {row.trial_days_remaining != null && (
                <span className="ml-1 text-zinc-500">({row.trial_days_remaining}d)</span>
              )}
            </span>
          ) : (
            fmt(row.next_billing_date)
          )}
        </td>
        {/* Price */}
        <td className="px-4 py-3 text-xs tabular-nums text-zinc-300">
          A${Number(row.monthly_price).toFixed(0)}/mo
        </td>
        {/* Expand toggle */}
        <td className="px-4 py-3 text-zinc-500">
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </td>
      </tr>

      {open && (
        <tr className="border-t border-zinc-800 bg-zinc-800/20" data-testid={`sub-detail-${row.user_id}`}>
          <td colSpan={6} className="px-6 py-4">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-8 gap-y-3 text-xs">
              <Detail label="User ID" value={row.user_id} mono />
              <Detail label="DB Status" value={row.db_status} />
              <Detail label="Cancel at period end" value={row.cancel_at_period_end ? "Yes" : "No"} />
              <Detail label="Trial end" value={fmt(row.trial_end)} />
              <Detail label="Trial days remaining" value={row.trial_days_remaining ?? "—"} />
              <Detail label="Next billing date" value={fmt(row.next_billing_date)} />
              <Detail label="Monthly price" value={`A$${Number(row.monthly_price).toFixed(2)}`} />
              <Detail
                label="Stripe subscription ID"
                value={row.stripe_subscription_id}
                mono
                link={row.stripe_subscription_id ? `${stripeBase}/subscriptions/${row.stripe_subscription_id}` : null}
              />
              <Detail
                label="Stripe customer ID"
                value={row.stripe_customer_id}
                mono
                link={row.stripe_customer_id ? `${stripeBase}/customers/${row.stripe_customer_id}` : null}
              />
              <Detail label="Payment method" value={row.has_payment_method ? "Yes" : "No"} />
              {row.has_payment_method && row.card_brand && (
                <Detail label="Card" value={`${row.card_brand} ···· ${row.card_last4}`} />
              )}
              <Detail label="Member since" value={fmt(row.created_at)} />
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const Detail = ({ label, value, mono, link }) => (
  <div>
    <p className="text-zinc-500 mb-0.5">{label}</p>
    {link ? (
      <a
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className={`text-[#4A7CF7] hover:underline inline-flex items-center gap-1 ${mono ? "font-mono" : ""}`}
        data-testid={`stripe-link-${label.replace(/\s/g, "-").toLowerCase()}`}
      >
        {value || "—"} <ExternalLink className="w-3 h-3" />
      </a>
    ) : (
      <p className={`text-zinc-200 ${mono ? "font-mono" : ""} break-all`}>{value ?? "—"}</p>
    )}
  </div>
);

// ─── main page ───────────────────────────────────────────────────────────────

const AdminSubscriptions = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subUsers, setSubUsers] = useState(null);
  const [subLoading, setSubLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/subscriptions`)
      .then((r) => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));

    axios.get(`${API}/admin/subscription-users`)
      .then((r) => setSubUsers(r.data))
      .catch(console.error)
      .finally(() => setSubLoading(false));
  }, []);

  // Client-side filter
  const filteredUsers = (() => {
    if (!subUsers?.users) return [];
    if (!activeFilter) return subUsers.users;
    const map = {
      trial: (r) => ["trial", "trialing"].includes(r.display_status),
      active: (r) => r.display_status === "active",
      no_payment_method: (r) => !r.has_payment_method && ["trial","trialing","active","cancelling"].includes(r.display_status),
      cancelling: (r) => r.display_status === "cancelling",
      paused_expired: (r) => ["paused","expired","cancelled"].includes(r.display_status),
    };
    return subUsers.users.filter(map[activeFilter] || (() => true));
  })();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) return <p className="text-zinc-500">Failed to load</p>;

  return (
    <div className="space-y-6" data-testid="admin-subscriptions-page">
      <div>
        <h1 className="text-2xl font-bold text-white tracking-tight">Subscriptions & Revenue</h1>
        <p className="text-sm text-zinc-500 mt-1">Payment tracking and subscription management</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-800 border border-zinc-800 rounded overflow-hidden">
        {[
          { label: "Active Subscriptions", value: data.active_subscriptions, icon: CreditCard, color: "text-emerald-400" },
          { label: "Trial Users",          value: data.trial_users,           icon: Users,      color: "text-blue-400" },
          { label: "Expired",              value: data.expired_users,         icon: Clock,      color: "text-red-400" },
          { label: "Total Revenue",        value: `$${data.total_revenue.toFixed(2)}`, icon: DollarSign, color: "text-[#002FA7]" },
        ].map((stat, idx) => {
          const Icon = stat.icon;
          return (
            <div key={idx} className="bg-zinc-900 p-5">
              <Icon className={`w-4 h-4 ${stat.color} mb-3`} />
              <p className="text-2xl font-bold tabular-nums text-white">{stat.value}</p>
              <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mt-1">{stat.label}</p>
            </div>
          );
        })}
      </div>

      {/* Revenue Breakdown */}
      <div className="bg-zinc-900 border border-zinc-800 rounded p-5">
        <h2 className="text-base font-medium text-white mb-3">Revenue Summary</h2>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <p className="text-xs text-zinc-500">Total Transactions</p>
            <p className="text-lg font-bold tabular-nums text-white">{data.total_transactions}</p>
          </div>
          <div>
            <p className="text-xs text-zinc-500">Avg per Transaction</p>
            <p className="text-lg font-bold tabular-nums text-white">
              ${data.total_transactions > 0 ? (data.total_revenue / data.total_transactions).toFixed(2) : "0.00"}
            </p>
          </div>
          <div>
            <p className="text-xs text-zinc-500">Monthly Recurring</p>
            <p className="text-lg font-bold tabular-nums text-white">
              ${(data.active_subscriptions * 39).toFixed(2)}
            </p>
          </div>
        </div>
      </div>

      {/* User Distribution */}
      <div className="bg-zinc-900 border border-zinc-800 rounded p-5">
        <h2 className="text-base font-medium text-white mb-4">User Distribution</h2>
        <div className="flex items-center gap-1 h-6 rounded overflow-hidden">
          {data.active_subscriptions > 0 && (
            <div className="bg-emerald-500 h-full transition-all"
              style={{ width: `${(data.active_subscriptions / data.total_users) * 100}%` }}
              title={`Active: ${data.active_subscriptions}`} />
          )}
          {data.trial_users > 0 && (
            <div className="bg-blue-500 h-full transition-all"
              style={{ width: `${(data.trial_users / data.total_users) * 100}%` }}
              title={`Trial: ${data.trial_users}`} />
          )}
          {data.expired_users > 0 && (
            <div className="bg-red-500 h-full transition-all"
              style={{ width: `${(data.expired_users / data.total_users) * 100}%` }}
              title={`Expired: ${data.expired_users}`} />
          )}
        </div>
        <div className="flex items-center gap-4 mt-2">
          <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-emerald-500" /><span className="text-xs text-zinc-400">Active ({data.active_subscriptions})</span></div>
          <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-blue-500" /><span className="text-xs text-zinc-400">Trial ({data.trial_users})</span></div>
          <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-red-500" /><span className="text-xs text-zinc-400">Expired ({data.expired_users})</span></div>
        </div>
      </div>

      {/* Recent Payments */}
      <div className="bg-zinc-900 border border-zinc-800 rounded overflow-hidden">
        <div className="p-4 border-b border-zinc-800">
          <h2 className="text-base font-medium text-white">Recent Payments</h2>
        </div>
        {data.recent_payments.length === 0 ? (
          <div className="p-8 text-center text-zinc-500 text-sm">No payments yet</div>
        ) : (
          <div className="divide-y divide-zinc-800">
            {data.recent_payments.map((payment, idx) => (
              <div key={idx} className="p-4 flex items-center justify-between hover:bg-zinc-800/50 transition-colors">
                <div className="flex items-center gap-3">
                  <Avatar className="w-8 h-8 border border-zinc-700">
                    <AvatarFallback className="bg-zinc-800 text-zinc-400 text-xs">
                      {getInitials(payment.user_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="text-sm font-medium text-white">{payment.user_name || "Unknown"}</p>
                    <p className="text-xs text-zinc-500">{payment.user_email || payment.email}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium tabular-nums text-emerald-400">
                    ${payment.amount?.toFixed(2) || "39.00"}
                  </p>
                  <p className="text-xs text-zinc-500">
                    {payment.created_at ? new Date(payment.created_at).toLocaleDateString() : "—"}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── All Subscriptions (Stripe-enriched) ── */}
      <div className="bg-zinc-900 border border-zinc-800 rounded overflow-hidden" data-testid="sub-users-table">
        <div className="p-4 border-b border-zinc-800 flex items-center justify-between gap-4 flex-wrap">
          <h2 className="text-base font-medium text-white">All Subscriptions</h2>
          {/* Filters */}
          <div className="flex items-center gap-1 flex-wrap" data-testid="sub-filters">
            {FILTERS.map((f) => (
              <button
                key={f.key ?? "all"}
                onClick={() => setActiveFilter(f.key)}
                data-testid={`filter-${f.key ?? "all"}`}
                className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                  activeFilter === f.key
                    ? "bg-[#002FA7] text-white"
                    : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-200"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {subLoading ? (
          <div className="flex items-center justify-center p-12">
            <div className="w-6 h-6 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin" />
            <span className="ml-3 text-sm text-zinc-500">Fetching Stripe data…</span>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="p-8 text-center text-zinc-500 text-sm">No users match this filter</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left" data-testid="sub-table">
              <thead>
                <tr className="border-b border-zinc-800">
                  <th className="px-4 py-3 text-xs uppercase tracking-[0.15em] text-zinc-500 font-medium">User</th>
                  <th className="px-4 py-3 text-xs uppercase tracking-[0.15em] text-zinc-500 font-medium">Status</th>
                  <th className="px-4 py-3 text-xs uppercase tracking-[0.15em] text-zinc-500 font-medium">Payment method</th>
                  <th className="px-4 py-3 text-xs uppercase tracking-[0.15em] text-zinc-500 font-medium">Trial end / Next billing</th>
                  <th className="px-4 py-3 text-xs uppercase tracking-[0.15em] text-zinc-500 font-medium">Price</th>
                  <th className="px-4 py-3 w-8" />
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((row) => (
                  <SubRow key={row.user_id} row={row} />
                ))}
              </tbody>
            </table>
            <div className="px-4 py-2 border-t border-zinc-800 text-xs text-zinc-600">
              {filteredUsers.length} user{filteredUsers.length !== 1 ? "s" : ""}
              {subUsers?.stripe_env === "test" && (
                <span className="ml-2 px-1.5 py-0.5 bg-amber-500/10 text-amber-400 border border-amber-500/20 rounded">
                  Stripe test mode
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSubscriptions;
